// University of Richmond: public college page (#2744), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Richmond's Common Data Set 2025-2026, posted by Institutional
//    Effectiveness one section per PDF (CDS2025-2026SectionC.pdf and
//    CDS2025-2026SectionG.pdf, linked from the Common Data Set page that the
//    SOURCES url names): C1 applicants, admits and enrollees for Fall 2025
//    with the residency table; C8 test policy for Fall 2027; C21/C22 early
//    plans; G1/G5 2026-2027 costs.
//  - Quirk: the CDS form labels its early decision counts "For the Fall 2024
//    entering class" (the template's own wording). The counts (1,154 applied,
//    421 admitted) differ from the 2024-2025 CDS's (1,175 and 397), but the
//    file never says fall 2025, so no early rate is computed from them.
//  - admission.richmond.edu: How to Apply (the four plans and deadlines),
//    Early Decision I and II, Early Action and Regular Decision (dates,
//    approximate notification dates, binding terms), Testing Requirements
//    for 2027 Applicants, and Arts Portfolio. The Early Action and Regular
//    Decision pages (process/early-action.html, process/regular-decision.html)
//    are linked from How to Apply, which the deadlines cite; they give the
//    approximate notification dates and May 1 reply date for those plans.
//    The ED rows' credential deadlines and deposit dates come from the Early
//    Decision page. None of these pages names a
//    change for fall 2028 entry; the testing page says the policy is reviewed
//    every April.
//  - College Scorecard, school id 233374 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items. Richmond lists no required fees ("Fees included
// in tuition") and leaves transportation blank, so neither appears.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Richmond Common Data Set 2025-2026',
        url: 'https://ifx.richmond.edu/research/common-data.html',
        read: '2026-10-01',
    },
    apply: {
        label: 'Richmond Admission, How to Apply',
        url: 'https://admission.richmond.edu/process/index.html',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Richmond Admission, Early Decision I and II',
        url: 'https://admission.richmond.edu/process/early-decision.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'Richmond Admission, Testing Requirements for 2027 Applicants',
        url: 'https://admission.richmond.edu/process/materials/test-optional.html',
        read: '2026-10-01',
    },
    arts: {
        label: 'Richmond Admission, Arts Portfolio',
        url: 'https://admission.richmond.edu/process/materials/arts-submissions.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?233374-University-of-Richmond',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-richmond',
    kbId: 'university_of_richmond',
    name: 'University of Richmond',
    shortName: 'Richmond',
    location: 'Richmond, VA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Richmond visual brand, color); decoration only.
    branding: {
        primary: '#990000',
        secondary: '#000066',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_richmond_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 16560,
        admitted: 3683,
        enrolled: 857,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2273, admitted: 495 },
            { group: 'Out-of-state', applied: 10183, admitted: 2908 },
            { group: 'International', applied: 4104, admitted: 280 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Richmond\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major. Every student starts in the School of Arts and Sciences and declares a major in the second year.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set reports 1,154 early decision applicants and 421 admitted, on a line its form labels the fall 2024 entering class, and gives no early action counts, so it gives no early acceptance rate for fall 2025.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Richmond, not to a school or major. One thing is optional:',
        rows: [
            { school: 'Music, theatre, dance and visual art', requirement: 'Optional: recordings or portfolios, uploaded in the Spider Portal by your plan\'s credentials deadline, are shared with the department for review. For Richmond Scholars or Presidential Scholarship consideration, the portfolio must be on file by December 1.', source: 'arts' },
        ],
        source: 'arts',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'About December 12', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'About January 23', binding: false },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'About February 13', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'About March 13', binding: false },
        ],
        note: 'All application materials are due November 15 for Early Decision I, December 1 for Early Action, January 15 for Early Decision II and February 15 for Regular Decision. Apply by December 1 to be considered for Richmond Scholars. Early Action and Regular Decision admits reply by May 1. Decision dates are the approximate notification dates on each plan\'s page, linked from How to Apply.',
        source: 'apply',
        binding: 'Early Decision I and II are binding: if admitted, you must enroll and withdraw your other applications, and you may not apply to another college\'s binding early decision plan. You may apply elsewhere under non-restrictive plans. You, a parent or guardian and your school counselor sign the Early Decision Agreement Form. Early decision admits confirm and pay the deposit by January 5 (ED I) or February 27 (ED II).',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Richmond is test-optional for first-year students entering in 2027. If you send the SAT, ACT or CLT, they are considered, and Richmond superscores the SAT and ACT; you choose on the application whether your scores are used. Students in home schools or online high schools must send SAT or ACT scores.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (fees included)', amount: 70725 },
            { item: 'Food and housing (on campus)', amount: 18815 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Other expenses', amount: 1780 },
        ],
        total: 92320,
        note: 'For a full-time undergraduate living on campus. The Common Data Set lists no separate required fees and leaves transportation blank.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 34256,
            byIncome: [
                { income: '$0 to $30,000', amount: 21456 },
                { income: '$30,001 to $48,000', amount: 10079 },
                { income: '$48,001 to $75,000', amount: 16251 },
                { income: '$75,001 to $110,000', amount: 44118 },
                { income: 'Over $110,000', amount: 44084 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Richmond\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
